import React, { useEffect, useMemo, useState } from "react";
import lineShape from "../assets/image/card-shape.svg";
import {
  FiArrowRight,
  FiSettings,
  FiCheck,
  FiLock,
  FiCreditCard,
  FiTruck,
  FiPlus,
  FiAlertTriangle,
} from "react-icons/fi";
import LayoutBuyerSeller from "../components/layout/LayoutBuyerSeller";
import { Plus } from "lucide-react";
import apiService from "../actions/apiService";
import CreateListingModal from "../components/models/CreateListingModal";
import TransactionManagementModal from "../components/models/TransactionManagementModal";
import ConfirmTransactionDoneModal from "../components/models/ConfirmTransactionDoneModal";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import carIcon from "../assets/icon/car.svg";
import bagIcon from "../assets/icon/bag.svg";
import watchIcon from "../assets/icon/watch.svg";
import homeIcon from "../assets/icon/home.svg";
import { getListingStatus } from "../utils/listingStatusUtils";

// Map API category keys to their icon assets
const CATEGORY_ICON_BY_KEY = {
  car: carIcon,
  bag: bagIcon,
  watch: watchIcon,
  real_estate: homeIcon,
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return "—";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";

  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return "just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60)
    return diffInMinutes === 1
      ? "about 1 minute ago"
      : `about ${diffInMinutes} minutes ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return diffInHours === 1
      ? "about 1 hour ago"
      : `about ${diffInHours} hours ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7)
    return diffInDays === 1
      ? "about 1 day ago"
      : `about ${diffInDays} days ago`;
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4)
    return diffInWeeks === 1
      ? "about 1 week ago"
      : `about ${diffInWeeks} weeks ago`;
  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12)
    return diffInMonths === 1
      ? "about 1 month ago"
      : `about ${diffInMonths} months ago`;
  const diffInYears = Math.floor(diffInDays / 365);
  return diffInYears === 1
    ? "about 1 year ago"
    : `about ${diffInYears} years ago`;
};

const getActivityMeta = (status) => {
  const normalized = (status || "").toLowerCase();
  if (["released", "paid", "accepted"].includes(normalized)) {
    return {
      Icon: FiCreditCard,
      badgeClass: "bg-[#8794E0] text-[#FFFFFF]",
      badgeText: "success",
    };
  }
  if (
    ["buyer_confirmed", "seller_confirmed", "confirmed", "shipped"].includes(
      normalized
    )
  ) {
    return {
      Icon: FiTruck,
      badgeClass: "bg-[#FFA9A2] text-[#840000]",
      badgeText: "in progress",
    };
  }
  return {
    Icon: FiLock,
    badgeClass: "bg-[#FFC49F] text-[#8B4513]",
    badgeText: normalized || "pending",
  };
};

const StatCard = ({
  title,
  metric,
  linkLabel,
  linkHref = "",
  trendPercent,
  trendCaption,
  trendDirection = "up",
}) => {
  const isUp = trendDirection === "up";
  return (
    <div className="bg-white w-full border min-w-[320px] lg:min-w-full border-[#DCEDFF] rounded-[12px] p-[20px]">
      <div className="flex items-start justify-between mb-0">
        <div>
          <p className="text-tag-d text-esdarkGray">{title}</p>
          <div className="mt-8 text-big-price md:text-big-price-d font-bold text-esblue">
            {metric}
          </div>
        </div>
        {linkHref && linkHref !== "#" ? (
          <a
            href={linkHref}
            className="text-tag-d text-esblue font-medium flex items-center gap-2"
          >
            {linkLabel}
            <FiArrowRight className="w-3.5 h-3.5" />
          </a>
        ) : (
          <button
            type="button"
            className="text-tag-d text-esblue font-medium flex items-center gap-2"
          >
            {linkLabel}
            <FiArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="mt-0 flex items-end justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[14px] px-[10px] py-1 rounded-[50px] bg-esTagColor text-esGray font-medium">
            {trendPercent}
          </span>
          <span className="text-[12px] text-[#000025] opacity-50">
            {trendCaption}
          </span>
        </div>
        <div className="relative w-10 h-8">
          <div className="absolute inset-0 flex items-end gap-1">
            {Array.from({ length: 5 }).map((_, i) => {
              const heights = [8, 14, 10, 16, 12];
              return (
                <div
                  key={i}
                  className={`${
                    isUp ? "bg-emerald-400/70" : "bg-rose-400/70"
                  } w-1.5 rounded-sm`}
                  style={{ height: heights[i] }}
                />
              );
            })}
          </div>
          {!isUp && (
            <span className="absolute -bottom-0.5 right-0 h-1.5 w-1.5 rounded-full bg-rose-500" />
          )}
        </div>
      </div>
    </div>
  );
};

const ListingCard = ({
  imageUrl,
  title,
  salePrice,
  escrowAmount,
  currencySymbol = "€",
  status = "pending",
  category,
  listingStatus,
  activeTransaction,
  currency = "EUR",
  listing, // Add listing prop to access full listing data
  onViewClick,
  onConfirmTransaction,
  onConfirmFunds,
  onSeeOffers,
  onSendOffers,
}) => {
  return (
    <div className="bg-white w-full min-w-full border border-[#DCEDFF] rounded-[24px] overflow-hidden p-4">
      <div className="relative h-44 bg-gray-100 rounded-[12px] overflow-hidden">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="h-full w-full bg-slate-200" />
        )}
        {listingStatus && (
          <span
            className={`absolute left-3 top-3 text-[14px] font-semibold px-2.5 py-1 rounded-[50px] capitalize whitespace-nowrap leading-none ${
              listingStatus.statusColor || "bg-gray-100 text-gray-700"
            }`}
          >
            {listingStatus.statusText}
          </span>
        )}
        {category !== "other" && CATEGORY_ICON_BY_KEY[category] && (
          <button className="absolute right-3 top-3 text-white op bg-[#1E1E1E40] rounded-[15px] h-[30px] w-[30px] flex items-center justify-center">
            <img
              src={CATEGORY_ICON_BY_KEY[category] || carIcon}
              alt={category}
              className="w-4 h-4 invert"
            />
          </button>
        )}
      </div>

      <div className="px-0 pt-5 pb-8">
        <h3 className="font-medium leading-5 line-clamp-2 text-gray-900 break-words">
          {title.length > 18 ? title.substring(0, 18) + "..." : title}
        </h3>
        <div className="mt-8 grid grid-cols-1 gap-4">
          <div>
            <div className="text-esDarkBlue text-caption opacity-50">
              Sale price
            </div>
            <div className="text-esblue font-semibold text-small-price-d">
              {salePrice.toFixed(2)} {currency || currencySymbol}
            </div>
          </div>
        </div>
      </div>

      <div className="px-0 pb-0 space-y-4">
        {listingStatus?.action === "confirm_transaction" && (
          <button
            type="button"
            onClick={() => onConfirmTransaction?.(listing)}
            className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            {listingStatus.actionText}
          </button>
        )}
        {listingStatus?.action === "confirm_funds" && (
          <button
            type="button"
            onClick={() => onConfirmFunds?.(listingStatus.transactionId)}
            className="w-full bg-[#B9FFCC] text-[#16653F] text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            {listingStatus.actionText}
          </button>
        )}
        {listingStatus?.action === "see_offers" && (
          <button
            type="button"
            onClick={() => onSeeOffers?.()}
            className="w-full bg-[#B9F2FF] text-[#2A8BF7] text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="20"
              viewBox="0 0 22 20"
              fill="none"
            >
              <path
                d="M18 19L21 16M21 16L18 13M21 16H15M14.5 1.29076C15.9659 1.88415 17 3.32131 17 5C17 6.67869 15.9659 8.11585 14.5 8.70924M11 13H7C5.13623 13 4.20435 13 3.46927 13.3045C2.48915 13.7105 1.71046 14.4892 1.30448 15.4693C1 16.2044 1 17.1362 1 19M12.5 5C12.5 7.20914 10.7091 9 8.5 9C6.29086 9 4.5 7.20914 4.5 5C4.5 2.79086 6.29086 1 8.5 1C10.7091 1 12.5 2.79086 12.5 5Z"
                stroke="#2A8BF7"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {listingStatus.actionText}
          </button>
        )}
        {listingStatus?.action === "send_offers" && (
          <button
            type="button"
            onClick={() => onSendOffers?.()}
            className="w-full border border-[#2A8BF7] bg-white text-esblue text-caption-d py-3 rounded-[8px] inline-flex text-[14px] font-[600] items-center justify-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            {listingStatus.actionText}
          </button>
        )}
        {listingStatus?.action === "completed" &&
          listingStatus.transactionId && (
            <Link
              to={`/seller/transactions/${listingStatus.transactionId}/invoice`}
              className="w-full border border-[#2A8BF7] px-4 py-3 rounded-[8px] text-[14px] text-[#2A8BF7] font-[600] flex items-center justify-center gap-2 bg-white hover:bg-blue-50 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="22"
                viewBox="0 0 18 22"
                fill="none"
              >
                <path
                  d="M11 1.26953V5.40007C11 5.96012 11 6.24015 11.109 6.45406C11.2049 6.64222 11.3578 6.7952 11.546 6.89108C11.7599 7.00007 12.0399 7.00007 12.6 7.00007H16.7305M11 16H5M13 12H5M17 8.98822V16.2C17 17.8802 17 18.7202 16.673 19.362C16.3854 19.9265 15.9265 20.3854 15.362 20.673C14.7202 21 13.8802 21 12.2 21H5.8C4.11984 21 3.27976 21 2.63803 20.673C2.07354 20.3854 1.6146 19.9265 1.32698 19.362C1 18.7202 1 17.8802 1 16.2V5.8C1 4.11984 1 3.27976 1.32698 2.63803C1.6146 2.07354 2.07354 1.6146 2.63803 1.32698C3.27976 1 4.11984 1 5.8 1H9.01178C9.74555 1 10.1124 1 10.4577 1.08289C10.7638 1.15638 11.0564 1.27759 11.3249 1.44208C11.6276 1.6276 11.887 1.88703 12.4059 2.40589L15.5941 5.59411C16.113 6.11297 16.3724 6.3724 16.5579 6.67515C16.7224 6.94356 16.8436 7.2362 16.9171 7.5423C17 7.88757 17 8.25445 17 8.98822Z"
                  stroke="#2A8BF7"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {listingStatus.actionText}
            </Link>
          )}
        <div className="bg-[#F5F8FF] rounded-[8px]">
          <button
            type="button"
            onClick={onViewClick}
            className="block w-full text-center text-caption-d text-esblue py-3 text-[14px] font-[600]"
          >
            View listing
          </button>
        </div>
      </div>
    </div>
  );
};

const ProfileOverview = ({
  hasRecent = false,
  isLoadingRecent = false,
  hasBankDetails = null,
  checkingBank = false,
  totalSales = 0,
  totalSold = 0,
  sellerRating = 0,
  statsLoading = false,
}) => {
  const { user } = useAuth();
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 text-white shadow-sm border border-white/20"
      style={{
        background: "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
      }}
    >
      {/* decorative tech lines */}
      <div
        className="absolute inset-0 left-[-110px] pointer-events-none"
        aria-hidden
      >
        <img
          src={lineShape}
          alt="ProtectPay"
          className="w-full opacity-35 invert h-full"
        />
      </div>

      <div className="flex items-center justify-between mb-8 relative z-10">
        <h3 className="text-big-price md:text-big-price-d !font-medium">
          Profile's overview
        </h3>
        <span className="bg-[#DCFCE7] text-[#16653F] border border-[#BBF7D0] text-[14px] font-semibold px-[10px] py-1 rounded-[50px] inline-flex items-center gap-1">
          <FiCheck className="w-3.5 h-3.5" />
          Verified
        </span>
      </div>

      <div className="flex items-center gap-4 relative z-10">
        {user?.profile_image_url ? (
          <img
            src={user?.profile_image_url}
            alt="avatar"
            className="w-16 h-16 object-cover rounded-full"
          />
        ) : (
          <span className="text-slate-600 rounded-full object-cover bg-slate-200  w-16 h-16 text-xl font-semibold flex items-center justify-center">
            {user?.firstName?.charAt(0) + user?.lastName?.charAt(0)}
          </span>
        )}
        <div className="flex-1">
          <div className="text-small-price-d opacity-75 font-normal">
            {user?.firstName} {user?.lastName}
          </div>
          <div className="text-[12px] opacity-75">
            {(() => {
              const createdAt = user?.created_at;
              if (!createdAt) {
                return "Member since N/A";
              }
              const date = new Date(createdAt);
              if (Number.isNaN(date.getTime())) {
                return "Member since N/A";
              }
              return `Member since ${date.toLocaleString(undefined, {
                month: "long",
                year: "numeric",
              })}`;
            })()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4 text-xs relative z-10">
        <div>
          <div className="text-white opacity-75 text-[12px]">Total sales</div>
          <div className="text-small-price-d font-bold">
            {statsLoading ? "0" : totalSales}
          </div>
        </div>
        {/* <div className="text-center">
          <div className="text-white opacity-75 text-[12px]">
            Seller's rating
          </div>
          <div className="text-small-price-d font-bold inline-flex items-center gap-1 justify-center">
            {statsLoading ? "0.0" : Number(sellerRating || 0).toFixed(1)}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="18"
              viewBox="0 0 20 18"
              fill="none"
            >
              <path
                d="M10 0.5L12.1329 7.06434H19.035L13.4511 11.1213L15.584 17.6857L10 13.6287L4.41604 17.6857L6.54892 11.1213L0.964963 7.06434H7.86712L10 0.5Z"
                fill="#FFF155"
              />
            </svg>
          </div>
        </div> */}
        <div className="text-right">
          <div className="text-white opacity-75 text-[12px]">Total sold</div>
          <div className="text-small-price-d font-bold">
            {statsLoading ? "0" : `${totalSold || 0} €`}
          </div>
        </div>
      </div>

      {/* Warning Banner - shown only after load and if there are no recent items */}
      {!checkingBank && hasBankDetails === false && (
        <div className="mt-4 relative z-10">
          <div className="bg-[#FFD9D9] rounded-[12px] p-2 flex items-center gap-1">
            <div className="flex-shrink-0">
              <FiAlertTriangle className="w-4 h-4 text-[#FF6E6E]" />
            </div>
            <div className="flex-1">
              <p className="text-[12px] text-[#FF6E6E]">
                Bank details missing! Go to{" "}
                <Link to={"/my-profile"} className="underline font-medium">
                  your profile
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-[1fr_auto] gap-3 items-center relative z-10">
        <Link
          to="/my-profile"
          className="w-full bg-[#F5F8FF] text-esblue text-nav-d font-semibold py-2.5 rounded-[8px] shadow-sm text-center hover:bg-blue-50 transition-colors"
        >
          See my profile
        </Link>
        <Link
          to="/account-details"
          className="p-2.5 rounded-[8px] text-white border border-[#FFFFFF40] transition-colors"
          aria-label="settings"
        >
          <FiSettings className="w-5 h-5 opacity-50" />
        </Link>
      </div>
    </div>
  );
};

const deriveCurrencySymbol = (currencySymbol, currencyCode) => {
  if (currencySymbol) return currencySymbol;
  if (currencyCode) {
    return currencyCode.length <= 3 ? currencyCode : currencyCode;
  }
  return "€";
};

const normalizeListing = (item) => {
  if (!item) return item;

  const releasedTransaction = Array.isArray(item.transactions)
    ? item.transactions.find((tx) => tx?.status?.toLowerCase() === "released")
    : null;

  const escrowAmount = releasedTransaction
    ? Number(releasedTransaction.seller_payout) || 0
    : Number(item.escrowAmount) || 0;

  // Prefer accepted purchase request offer amount if available
  const purchaseRequests =
    item?.purchase_requests || item?.purchaseRequests || [];
  const acceptedRequest = Array.isArray(purchaseRequests)
    ? purchaseRequests.find((pr) => pr?.status?.toLowerCase() === "accepted")
    : null;
  const offerPrice = Number(acceptedRequest?.offer_amount);
  const numericPrice = Number.isFinite(offerPrice)
    ? offerPrice
    : Number(item.price);
  const categoryMap = {
    car: "car",
    bag: "bag",
    watch: "watch",
    real_estate: "real_estate",
    other: "other",
  };

  // Get listing status using shared utility
  const listingStatus = getListingStatus(item);

  return {
    ...item,
    price: Number.isFinite(numericPrice)
      ? numericPrice
      : Number(item.price) || 0,
    escrowAmount,
    currencySymbol: deriveCurrencySymbol(item.currency_symbol, item.currency),
    category: categoryMap[item.category] || item.category,
    listingStatus,
    activeTransaction: item.transactions?.[0] || null,
  };
};

const SellerPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recentItems, setRecentItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [recentError, setRecentError] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedConfirmListing, setSelectedConfirmListing] = useState(null);
  const [confirmingTransaction, setConfirmingTransaction] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [checkingBank, setCheckingBank] = useState(false);
  const [hasBankDetails, setHasBankDetails] = useState(null);
  const [escrowSummary, setEscrowSummary] = useState(null);
  const [escrowSummaryError, setEscrowSummaryError] = useState(null);
  const [sellerStats, setSellerStats] = useState({
    totalSales: 0,
    totalSoldValue: 0,
    sellerRating: 0,
  });
  const [sellerStatsLoading, setSellerStatsLoading] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchRecent = async () => {
      setLoadingRecent(true);
      setRecentError(null);
      try {
        const res = await apiService.get("/items", {
          page: 1,
          limit: 9,
          sort: "created_at",
          order: "desc",
        });
        if (!active) return;
        const list =
          res?.data?.items || res?.data?.data?.items || res?.data || [];
        const normalized = Array.isArray(list)
          ? list.map((item) => normalizeListing(item))
          : [];
        setRecentItems(normalized);
        setTotalItems(res?.data?.pagination?.totalItems);
      } catch (e) {
        if (active) setRecentError("Failed to load recent listings");
      } finally {
        if (active) setLoadingRecent(false);
      }
    };
    fetchRecent();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const checkBank = async () => {
      if (!user?.id) return;
      setCheckingBank(true);
      try {
        const res = await apiService.get("/bank-accounts/check");
        if (!active) return;
        setHasBankDetails(!!res?.data?.exists);
      } catch (_) {
        if (active) setHasBankDetails(null);
      } finally {
        if (active) setCheckingBank(false);
      }
    };
    checkBank();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchEscrowSummary = async () => {
      if (!user?.id) return;
      setEscrowSummaryError(null);
      try {
        const res = await apiService.get("/transactions/escrow-summary");
        if (!active) return;
        if (res?.success && res.data) {
          setEscrowSummary(res.data);
        } else {
          setEscrowSummary(null);
        }
      } catch (_) {
        if (active) {
          setEscrowSummary(null);
        }
      }
    };
    fetchEscrowSummary();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchSellerStats = async () => {
      if (!user?.id) return;
      setSellerStatsLoading(true);
      try {
        const res = await apiService.get("/transactions/seller-stats");
        if (!active) return;
        if (res?.success && res.data) {
          setSellerStats({
            totalSales: res.data.totalSales || 0,
            totalSoldValue: res.data.totalSoldValue || 0,
            sellerRating:
              typeof res.data.sellerRating === "number"
                ? res.data.sellerRating
                : 0,
          });
        } else if (active) {
          setSellerStats({
            totalSales: 0,
            totalSoldValue: 0,
            sellerRating: 0,
            currencySymbol: "€",
          });
        }
      } catch (_) {
        if (active) {
          setSellerStats({
            totalSales: 0,
            totalSoldValue: 0,
            sellerRating: 0,
            currencySymbol: "€",
          });
        }
      } finally {
        if (active) {
          setSellerStatsLoading(false);
        }
      }
    };
    fetchSellerStats();
    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    const fetchSellerActivity = async () => {
      if (!user?.id) return;
      setActivityLoading(true);
      try {
        const res = await apiService.get("/transactions/seller-activity", {
          limit: 3,
        });
        if (!active) return;
        setRecentActivity(res?.data?.activity || []);
      } catch (_) {
        if (active) setRecentActivity([]);
      } finally {
        if (active) setActivityLoading(false);
      }
    };
    fetchSellerActivity();
    return () => {
      active = false;
    };
  }, [user?.id]);

  const imageBase = process.env.REACT_APP_IMAGE_URL || "";

  const totalEscrowValue = useMemo(() => {
    if (escrowSummary && typeof escrowSummary.totalEscrow === "number") {
      return Number(escrowSummary.totalEscrow) || 0;
    }
    return recentItems.reduce(
      (sum, item) => sum + Number(item.escrowAmount),
      0
    );
  }, [escrowSummary, recentItems]);

  const primaryCurrencySymbol = useMemo(() => {
    if (escrowSummary?.currencySymbol) {
      return escrowSummary.currencySymbol;
    }
    return (
      recentItems.find((item) => item?.currencySymbol)?.currencySymbol || "€"
    );
  }, [escrowSummary, recentItems]);

  // Check if there are any new offers received
  const hasNewOffers = useMemo(() => {
    return recentItems.some((item) => {
      const purchaseRequests = Array.isArray(item.purchase_requests)
        ? item.purchase_requests
        : Array.isArray(item.offers)
        ? item.offers
        : [];
      return purchaseRequests.some((pr) => pr?.status === "pending");
    });
  }, [recentItems]);

  const handleCreateListing = async (listingData) => {
    setCreateLoading(true);
    setCreateError(null);
    try {
      const formData = new FormData();
      formData.append("title", listingData.title);
      formData.append("category", listingData.category);
      if (listingData.category === "other" && listingData.customCategory) {
        formData.append("custom_category", listingData.customCategory);
      }
      formData.append("description", listingData.description);
      formData.append("price", String(listingData.price));
      formData.append("location", listingData.location || "");
      // Always append plate_number when category is car (even if empty) so backend can validate
      if (listingData.category === "car") {
        formData.append("plate_number", listingData.plateNumber || "");
      } else if (listingData.plateNumber) {
        formData.append("plate_number", listingData.plateNumber);
      }
      if (Array.isArray(listingData.additionalProperties)) {
        const customProps = {};
        listingData.additionalProperties.forEach((p) => {
          customProps[p.name] = p.value;
        });
        formData.append("custom_properties", JSON.stringify(customProps));
      }
      if (Array.isArray(listingData.images)) {
        listingData.images.forEach((file) => formData.append("images", file));
      }

      const result = await apiService.postFormData("/items", formData);
      if (result?.success) {
        setCreateOpen(false);
        const rawItem = result?.data?.item || result?.data;
        if (rawItem) {
          const normalized = normalizeListing(rawItem);
          setRecentItems((prev) => [normalized, ...prev].slice(0, 3));
          // Open Transaction Management Modal with the newly created item
          setSelectedItemId(rawItem.id);
          setTransactionModalOpen(true);
        }
      } else {
        setCreateError(result?.error || "Failed to create listing");
      }
    } catch (e) {
      setCreateError("Network error. Please try again.");
    } finally {
      setCreateLoading(false);
    }
  };

  // Refresh recent items
  const refreshRecentItems = async () => {
    try {
      const res = await apiService.get("/items", {
        page: 1,
        limit: 9,
        sort: "created_at",
        order: "desc",
      });
      const list =
        res?.data?.items || res?.data?.data?.items || res?.data || [];
      const normalized = Array.isArray(list)
        ? list.map((item) => normalizeListing(item))
        : [];
      setRecentItems(normalized);
    } catch (error) {
      console.error("Error refreshing recent items:", error);
    }
  };

  // Handler for opening confirm transaction modal
  const handleOpenConfirmModal = (listing) => {
    setSelectedConfirmListing(listing);
    setConfirmModalOpen(true);
    setConfirmError("");
  };

  // Handler for confirming transaction (seller confirms delivery) - called from modal
  const handleConfirmTransaction = async () => {
    if (!selectedConfirmListing?.listingStatus?.transactionId) {
      setConfirmError("Transaction ID is missing.");
      return;
    }

    setConfirmingTransaction(true);
    setConfirmError("");

    try {
      const confirmRes = await apiService.post(
        `/transactions/${selectedConfirmListing.listingStatus.transactionId}/confirm-seller`
      );
      if (confirmRes.success) {
        setConfirmModalOpen(false);
        setSelectedConfirmListing(null);
        // Wait a moment for backend to process, then refresh
        await new Promise((resolve) => setTimeout(resolve, 500));
        await refreshRecentItems();
      } else {
        setConfirmError(confirmRes.error || "Failed to confirm transaction.");
      }
    } catch (error) {
      setConfirmError("Failed to confirm transaction. Please try again.");
      console.error("Error confirming transaction:", error);
    } finally {
      setConfirmingTransaction(false);
    }
  };

  // Handler for confirming funds reception
  const handleConfirmFunds = async (transactionId) => {
    if (!transactionId) return;
    try {
      const confirmRes = await apiService.post(
        `/transactions/${transactionId}/confirm-funds`
      );
      if (confirmRes.success) {
        await refreshRecentItems();
      }
    } catch (error) {
      console.error("Error confirming funds:", error);
    }
  };

  // Handler for see offers - navigate to requests page
  const handleSeeOffers = () => {
    navigate("/seller/requests");
  };

  // Handler for send offers - open transaction modal
  const handleSendOffers = (item) => {
    setSelectedItemId(item.id);
    setTransactionModalOpen(true);
  };

  return (
    <LayoutBuyerSeller>
      <div className="absolute lg:hidden top-0 left-0 h-72 w-full bg-blue-gradient"></div>
      <div className="relative z-[10]">
        <div className="mb-8 px-5">
          <h2 className="md:text-page-title-d text-page-title !font-[600] text-white lg:text-[#1E1E1E] font-rubik">
            Welcome back, {user?.firstName} {user?.lastName}{" "}
            <span role="img" aria-label="waving hand">
              👋
            </span>
          </h2>
          <p className="text-small-price-d font-normal opacity-50 text-white lg:text-[#1E1E1E] mt-4">
            Here is what's happening with your escrow transactions this week.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2 px-5 lg:px-0 lg:ps-5 order-2 lg:order-1">
            <div className="mb-8 text-big-price md:text-big-price-d !font-medium text-esdarkGray">
              General overview
            </div>
            <div className="flex overflow-y-auto lg:grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                title="Total in escrow"
                metric={
                  <span>
                    {totalEscrowValue.toFixed(2)} {primaryCurrencySymbol}
                  </span>
                }
                linkLabel="My transactions"
                trendPercent="+0%"
                trendCaption="vs last month"
                trendDirection="up"
                linkHref="/seller/transactions"
              />
              {escrowSummaryError && (
                <span className="text-xs text-red-500">
                  {escrowSummaryError}
                </span>
              )}
              <StatCard
                title="Active listings"
                metric={<span>{totalItems}</span>}
                linkLabel="My listings"
                trendPercent="+0%"
                trendCaption="vs last month"
                trendDirection="up"
                linkHref="/seller/listings"
              />
            </div>
          </div>
          <div className="ms-0 bg-[#F5F8FF] lg:bg-transparent rounded-tl-[25px] lg:rounded-tl-[0px] rounded-tr-[25px] lg:rounded-tr-[0px] p-5 lg:p-0 lg:ms-4 order-1 lg:order-2">
            <ProfileOverview
              hasRecent={(recentItems || []).length > 0}
              isLoadingRecent={loadingRecent}
              hasBankDetails={hasBankDetails}
              checkingBank={checkingBank}
              totalSales={sellerStats.totalSales}
              totalSold={sellerStats.totalSoldValue}
              sellerRating={sellerStats.sellerRating}
              statsLoading={sellerStatsLoading}
            />
          </div>
        </div>

        {/* Recent listing + Recent activity */}
        <div className="mt-6 grid grid-cols-1 px-5 lg:px-0 lg:ps-5  lg:grid-cols-3 gap-6 items-start">
          {/* Left: Recent listing (spans 2) */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-[#DCEDFF] rounded-xl p-5">
              <div>
                <div>
                  <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
                    <h4 className="md:text-big-price-d text-big-price font-semibold text-gray-900">
                      Recent listing
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">
                      <button
                        onClick={() => setCreateOpen(true)}
                        className="w-full text-white py-2 px-3 rounded-lg font-[600] text-sm inline-flex items-center justify-center gap-2"
                        style={{
                          background:
                            "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
                        }}
                      >
                        <FiPlus className="w-4 h-4" />
                        Create a new listing
                      </button>
                      <Link
                        to="/seller/listings"
                        className="w-full bg-[#F5F8FF] text-[#2A8BF7] py-2 px-3 rounded-lg font-[600] text-sm inline-flex items-center justify-center gap-2"
                      >
                        See all my listings
                        <FiArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                  {/* New Offer Alert */}
                  {hasNewOffers && (
                    <div
                      className="mb-8 bg-[#FFD9D9] border border-red-200 rounded-[12px] px-[12px] py-[6.5px] flex items-center gap-3 cursor-pointer"
                      onClick={() => navigate("/seller/requests")}
                    >
                      <div className="flex-shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#FF6E6E]"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#FF6E6E] font-medium">
                          You received a new offer, go check your buyer's
                          requests.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className={`flex lg:grid overflow-y-auto gap-4 ${
                    recentItems.length === 0
                      ? "justify-center"
                      : "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                  }`}
                >
                  {loadingRecent && (
                    <div className="text-sm my-4 text-gray-500">
                      Loading items...
                    </div>
                  )}
                  {recentError && (
                    <div className="text-sm text-red-600">{recentError}</div>
                  )}
                  {!loadingRecent &&
                    !recentError &&
                    recentItems.length === 0 && (
                      <>
                        <div className="space-y-8 py-[44px] text-center">
                          <div className="mx-auto w-[48px] h-[48px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="48"
                              height="48"
                              viewBox="0 0 48 48"
                              fill="none"
                            >
                              <path
                                d="M28 4H12C10.9391 4 9.92172 4.42143 9.17157 5.17157C8.42143 5.92172 8 6.93913 8 8V40C8 41.0609 8.42143 42.0783 9.17157 42.8284C9.92172 43.5786 10.9391 44 12 44H36C37.0609 44 38.0783 43.5786 38.8284 42.8284C39.5786 42.0783 40 41.0609 40 40V16M28 4L40 16M28 4V16H40M24 36V24M18 30H30"
                                stroke="#2A8BF7"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <h4 className="md:text-big-price-d font-rubik text-big-price font-semibold text-esdarkGray">
                            Recent activity
                          </h4>
                          <p className="body-d opacity-50 max-w-[500px] mx-auto text-esdarkGray">
                            Start building your luxury marketplace by creating
                            your first listing. Showcase high-value items with
                            secure escrow protection.
                          </p>
                          <Link
                            to="/seller/transactions"
                            className="bg-blue-gradient text-white text-subsection-title font-[600] px-3 py-[12.5px] rounded-[8px] inline-flex items-center justify-center gap-4"
                          >
                            {" "}
                            <Plus className="w-3.5 h-3.5" />
                            See my transactions
                          </Link>
                        </div>
                      </>
                    )}
                  {recentItems.map((it) => (
                    <ListingCard
                      key={it.id}
                      title={it.title}
                      salePrice={it.price}
                      escrowAmount={it.escrowAmount}
                      currencySymbol={
                        it.currencySymbol || primaryCurrencySymbol
                      }
                      currency={it.currency || "EUR"}
                      status={it.status || "available"}
                      category={it.category || it.custom_category}
                      listingStatus={it.listingStatus}
                      activeTransaction={it.activeTransaction}
                      imageUrl={
                        it.images && it.images[0]?.file_url
                          ? imageBase + it.images[0].file_url
                          : ""
                      }
                      onViewClick={() => {
                        navigate(`/seller/listings/${it.id}`);
                      }}
                      listing={it}
                      onConfirmTransaction={handleOpenConfirmModal}
                      onConfirmFunds={handleConfirmFunds}
                      onSeeOffers={handleSeeOffers}
                      onSendOffers={() => handleSendOffers(it)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Recent activity */}
          <div>
            <div className="bg-white border border-[#DCEDFF] rounded-[12px] p-5">
              <h4 className="md:text-big-price-d text-big-price text-esdarkGray mb-8">
                Recent activity
              </h4>
              <div>
                {activityLoading ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    Loading...
                  </div>
                ) : recentActivity.length === 0 ? (
                  <div className="py-4 text-center text-sm text-gray-500">
                    No recent activity
                  </div>
                ) : (
                  recentActivity.map((activity) => {
                    const { Icon, badgeClass, badgeText } = getActivityMeta(
                      activity.status
                    );
                    return (
                      <div
                        key={activity.id}
                        className="first:pt-0 py-4 last:pb-0 border-b border-[#1E1E1E0D] last:border-b-0"
                      >
                        <div className="px-0 lg:px-5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className="mt-0.5 text-blue-600">
                                <Icon className="w-5 h-5" />
                              </span>
                              <div className="flex items-center gap-2">
                                <p className="text-tag-d font-semibold text-esdarkGray">
                                  {activity.title || "Transaction update"}
                                </p>
                              </div>
                            </div>
                            <span
                              className={`text-tag md:text-tag-d px-[10px] py-1 rounded-[50px] ${badgeClass}`}
                            >
                              {badgeText}
                            </span>
                          </div>
                          <div className="mt-2">
                            <p className="text-body-d text-esDarkBlue opacity-50">
                              Transaction reference:{" "}
                              {activity.reference_code || activity.id}
                            </p>
                            <div className="flex items-center justify-between mt-2 gap-2">
                              <p className="text-[12px] text-esDarkBlue opacity-50">
                                {formatRelativeTime(activity.updated_at)}
                              </p>
                              <div className="text-esblue text-small-price-d font-bold">
                                {activity.amount != null
                                  ? `${Number(
                                      activity.amount
                                    ).toLocaleString()}€`
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateListingModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreateListing}
        isLoading={createLoading}
        error={createError}
      />
      <TransactionManagementModal
        open={transactionModalOpen}
        onClose={() => setTransactionModalOpen(false)}
        transactionData={null}
        itemId={selectedItemId}
        onInviteSent={refreshRecentItems}
      />
      <ConfirmTransactionDoneModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setSelectedConfirmListing(null);
          setConfirmError("");
        }}
        onConfirm={handleConfirmTransaction}
        isLoading={confirmingTransaction}
        errorText={confirmError}
      />
    </LayoutBuyerSeller>
  );
};

export default SellerPage;
